import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, Send, MessageSquare, Users, CreditCard, Plus,
  CheckCircle2, XCircle, Clock, Search, Zap, Crown, Key, Settings
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/context/auth";
import { format } from "date-fns";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const TEMPLATES = [
  {
    id: "promo",
    label: "🎉 Promo Alert",
    text: "Hi {name}! Special offer just for you at {store}. Visit us today and enjoy exclusive discounts. Limited time only!",
  },
  {
    id: "loyalty",
    label: "⭐ Loyalty Points",
    text: "Hi {name}! You have loyalty points waiting at {store}. Come in and redeem them on your next purchase. Thank you!",
  },
  {
    id: "restock",
    label: "📦 New Stock",
    text: "Hi {name}! New items just arrived at {store}. Come check out our latest collection. We can't wait to see you!",
  },
  {
    id: "reminder",
    label: "🔔 Payment Reminder",
    text: "Hi {name}, this is a friendly reminder from {store} about your outstanding balance. Please visit us or call to settle. Thank you.",
  },
  {
    id: "thanks",
    label: "🙏 Thank You",
    text: "Thank you for shopping at {store}, {name}! We appreciate your loyalty. See you again soon!",
  },
];

interface Customer { id: number; name: string; phone: string | null }
interface SmsLog {
  id: number; recipients: number; message: string; template: string | null;
  status: string; creditsUsed: number; sentAt: string;
}
interface Credits {
  balance: number; totalSent: number; monthlyAllowance: number;
  allowanceResetAt: string | null; creditPricePerSms: number;
  isEnabled: boolean; plan: string; senderName: string | null;
  hasCustomTermiiKey: boolean;
}

export default function SmsPage() {
  const { store } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState("compose");
  const [message, setMessage] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupCredits, setTopupCredits] = useState("100");
  const [topupLoading, setTopupLoading] = useState(false);
  const [senderOpen, setSenderOpen] = useState(false);
  const [senderInput, setSenderInput] = useState("");
  const [senderSaving, setSenderSaving] = useState(false);
  const [termiiKeyOpen, setTermiiKeyOpen] = useState(false);
  const [termiiKeyInput, setTermiiKeyInput] = useState("");
  const [termiiKeySaving, setTermiiKeySaving] = useState(false);

  const { data: credits, isLoading: creditsLoading } = useQuery<Credits>({
    queryKey: ["sms-credits"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/sms/credits`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const { data: customers = [], isLoading: customersLoading } = useQuery<Customer[]>({
    queryKey: ["sms-customers"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/sms/customers`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: credits?.isEnabled,
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery<SmsLog[]>({
    queryKey: ["sms-logs"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/sms/logs`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: credits?.isEnabled,
  });

  // Verify top-up on return from Paystack
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("reference");
    if (ref) {
      fetch(`${BASE}/api/sms/topup/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reference: ref }),
      }).then(async r => {
        const d = await r.json();
        if (r.ok) {
          toast({ title: `${d.creditsAdded} SMS credits added!` });
          queryClient.invalidateQueries({ queryKey: ["sms-credits"] });
          window.history.replaceState({}, "", window.location.pathname);
        }
      });
    }
  }, []);

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (c.phone ?? "").includes(customerSearch)
  );

  function applyTemplate(tpl: typeof TEMPLATES[0]) {
    setSelectedTemplate(tpl.id);
    const text = tpl.text
      .replace("{store}", store?.name ?? "our store")
      .replace(/\{name\}/g, "Valued Customer");
    setMessage(text);
  }

  function toggleCustomer(id: number) {
    setSelectedCustomers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  function selectAll() {
    setSelectedCustomers(filteredCustomers.map(c => c.id));
  }

  function clearAll() { setSelectedCustomers([]); }

  const selectedPhones = customers
    .filter(c => selectedCustomers.includes(c.id) && c.phone)
    .map(c => c.phone!);

  async function handleSend() {
    if (!message.trim()) { toast({ title: "Write a message first", variant: "destructive" }); return; }
    if (selectedPhones.length === 0) { toast({ title: "Select at least one customer", variant: "destructive" }); return; }
    if (message.length > 160) { toast({ title: "Message too long (max 160 characters)", variant: "destructive" }); return; }

    setSending(true);
    try {
      const res = await fetch(`${BASE}/api/sms/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message, phoneNumbers: selectedPhones, template: selectedTemplate }),
      });
      const data = await res.json();
      if (!res.ok && data.upgrade === true) {
        toast({
          title: "Upgrade required",
          description: data.error,
          action: <ToastAction altText="View plans" onClick={() => { window.location.href = `${BASE}/subscribe`; }}>View plans</ToastAction>,
        });
        return;
      }
      if (!res.ok) throw new Error(data.error);
      toast({ title: data.message, description: `${data.creditsUsed} credits used · ${data.remainingBalance} remaining` });
      setMessage("");
      setSelectedCustomers([]);
      setSelectedTemplate(null);
      queryClient.invalidateQueries({ queryKey: ["sms-credits"] });
      queryClient.invalidateQueries({ queryKey: ["sms-logs"] });
      setTab("logs");
    } catch (e: any) {
      toast({ title: e.message || "Failed to send", variant: "destructive" });
    } finally {
      setSending(false);
    }
  }

  async function handleSaveSender(resetToDefault = false) {
    setSenderSaving(true);
    try {
      const res = await fetch(`${BASE}/api/sms/sender`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ senderName: resetToDefault ? "" : senderInput.trim().replace(/\s+/g, "").slice(0, 11) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast({ title: resetToDefault ? "Sender reset to platform default" : `Sender name set to "${data.senderName}"` });
      queryClient.invalidateQueries({ queryKey: ["sms-credits"] });
      setSenderOpen(false);
    } catch (e: any) {
      toast({ title: e.message || "Failed to save", variant: "destructive" });
    } finally {
      setSenderSaving(false);
    }
  }

  async function handleSaveTermiiKey(clear = false) {
    setTermiiKeySaving(true);
    try {
      const res = await fetch(`${BASE}/api/sms/termii-key`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ termiiApiKey: clear ? "" : termiiKeyInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast({ title: clear ? "Reverted to platform Termii account" : "Custom Termii API key saved" });
      queryClient.invalidateQueries({ queryKey: ["sms-credits"] });
      setTermiiKeyOpen(false);
      setTermiiKeyInput("");
    } catch (e: any) {
      toast({ title: e.message || "Failed to save", variant: "destructive" });
    } finally {
      setTermiiKeySaving(false);
    }
  }

  async function handleTopup() {
    const count = Number(topupCredits);
    if (!count || count < 10) { toast({ title: "Minimum 10 credits", variant: "destructive" }); return; }
    setTopupLoading(true);
    try {
      const res = await fetch(`${BASE}/api/sms/topup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credits: count, callbackUrl: window.location.href }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.location.href = data.paymentUrl;
    } catch (e: any) {
      toast({ title: e.message || "Failed", variant: "destructive" });
      setTopupLoading(false);
    }
  }

  const statusIcon = (s: string) => {
    if (s === "sent") return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    if (s === "failed") return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
  };

  if (creditsLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  // Plan gate
  if (!credits?.isEnabled) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center py-16 space-y-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
            <Crown className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold">Bulk SMS</h1>
          <p className="text-muted-foreground">
            Send branded SMS to your customers directly from RetailPulsePOS.
            Monthly credits are included with <strong>Basic</strong>, <strong>Pro</strong>, and <strong>Enterprise</strong>.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-6 text-left max-w-3xl mx-auto">
            {[
              { plan: "Basic", credits: "100 SMS/month", price: "₦7,500/mo" },
              { plan: "Pro", credits: "200 SMS/month", price: "₦12,000/mo" },
              { plan: "Enterprise", credits: "500 SMS/month", price: "₦28,000/mo" },
            ].map(p => (
              <Card key={p.plan} className="border-2 border-primary/20">
                <CardContent className="p-4">
                  <p className="font-bold text-lg">{p.plan}</p>
                  <p className="text-sm text-emerald-600 font-medium">{p.credits}</p>
                  <p className="text-sm text-muted-foreground">{p.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <Button className="mt-4" onClick={() => window.location.href = `${BASE}/subscribe`}>
            Upgrade Now
          </Button>
          <p className="text-xs text-muted-foreground">Extra credits available at ₦3 per SMS</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header + credits */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Bulk SMS</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Send branded messages to your customers.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="bg-card border rounded-xl px-4 py-2 text-sm">
            <span className="text-muted-foreground">Credits: </span>
            <span className="font-bold text-lg">{credits.balance}</span>
            <span className="text-muted-foreground"> / {credits.monthlyAllowance}/mo</span>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setTopupOpen(true)}>
            <Plus className="w-4 h-4" /> Top Up
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setSenderInput(credits.senderName ?? ""); setSenderOpen(true); }}>
            <Settings className="w-4 h-4" /> Sender Name
          </Button>
          <Button variant="outline" size="sm" className={`gap-1.5 ${credits.hasCustomTermiiKey ? "border-emerald-500 text-emerald-700" : ""}`} onClick={() => { setTermiiKeyInput(""); setTermiiKeyOpen(true); }}>
            <Key className="w-4 h-4" />
            {credits.hasCustomTermiiKey ? "Own Termii Key ✓" : "Set Termii Key"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Credits Left</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold">{credits.balance}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Sent</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold">{credits.totalSent}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Customers w/ Phone</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold">{customers.length}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="compose" className="gap-2"><Send className="w-4 h-4" /> Compose</TabsTrigger>
          <TabsTrigger value="logs" className="gap-2"><MessageSquare className="w-4 h-4" /> History</TabsTrigger>
        </TabsList>

        {/* ─── COMPOSE ─── */}
        <TabsContent value="compose" className="mt-4 space-y-5">
          {/* Templates */}
          <div>
            <p className="text-sm font-medium mb-2">Quick Templates</p>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  className={`text-sm px-3 py-1.5 rounded-lg border transition-all ${
                    selectedTemplate === t.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card hover:bg-muted border-border"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">Message</label>
              <span className={`text-xs ${message.length > 160 ? "text-red-500 font-semibold" : "text-muted-foreground"}`}>
                {message.length}/160
              </span>
            </div>
            {store?.name && (
              <div className="flex items-center gap-1.5 mb-1.5 px-3 py-1.5 bg-primary/5 border border-primary/20 rounded-lg text-xs text-primary">
                <span className="font-semibold">[{store.name}]</span>
                <span className="text-muted-foreground">will be added to the start of every message</span>
              </div>
            )}
            <Textarea
              placeholder="Type your message here..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={4}
              className="resize-none"
            />
            {message.length > 160 && (
              <p className="text-xs text-red-500 mt-1">Message too long — max 160 characters</p>
            )}
          </div>

          {/* Customer picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">
                Recipients
                {selectedCustomers.length > 0 && (
                  <span className="ml-2 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                    {selectedCustomers.length} selected
                  </span>
                )}
              </label>
              <div className="flex gap-2 text-xs">
                <button onClick={selectAll} className="text-primary hover:underline">Select all</button>
                <span className="text-muted-foreground">·</span>
                <button onClick={clearAll} className="text-muted-foreground hover:underline">Clear</button>
              </div>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {customersLoading ? (
              <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No customers with phone numbers found.
                <br />Add phone numbers in the Customers section.
              </div>
            ) : (
              <div className="border rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                {filteredCustomers.map(c => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 cursor-pointer border-b last:border-b-0 transition-colors"
                  >
                    <Checkbox
                      checked={selectedCustomers.includes(c.id)}
                      onCheckedChange={() => toggleCustomer(c.id)}
                    />
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.phone}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Send bar */}
          <div className="flex items-center justify-between bg-muted/40 rounded-xl px-4 py-3">
            <div className="text-sm text-muted-foreground">
              {selectedPhones.length > 0
                ? <><span className="font-semibold text-foreground">{selectedPhones.length} SMS</span> will be sent · {selectedPhones.length} credit{selectedPhones.length > 1 ? "s" : ""} used</>
                : "Select recipients above"}
            </div>
            <Button
              onClick={handleSend}
              disabled={sending || selectedPhones.length === 0 || !message.trim() || message.length > 160}
              className="gap-2"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send SMS
            </Button>
          </div>
        </TabsContent>

        {/* ─── HISTORY ─── */}
        <TabsContent value="logs" className="mt-4">
          {logsLoading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No SMS sent yet. Go to Compose to send your first message.</p>
            </div>
          ) : (
            <div className="bg-card border rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead className="text-right">Recipients</TableHead>
                    <TableHead className="text-right">Credits</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {format(new Date(log.sentAt), "dd MMM, h:mm a")}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm truncate">{log.message}</p>
                      </TableCell>
                      <TableCell>
                        {log.template
                          ? <Badge variant="secondary" className="text-xs">{TEMPLATES.find(t => t.id === log.template)?.label ?? log.template}</Badge>
                          : <span className="text-xs text-muted-foreground">Custom</span>}
                      </TableCell>
                      <TableCell className="text-right font-medium">{log.recipients}</TableCell>
                      <TableCell className="text-right">{log.creditsUsed}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {statusIcon(log.status)}
                          <span className="text-sm capitalize">{log.status}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Sender name dialog */}
      <Dialog open={senderOpen} onOpenChange={setSenderOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>SMS Sender Name</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 space-y-1">
              <p className="font-bold">⚠️ Sender name must be pre-registered with Termii</p>
              <p>Any name you set here must first be approved by Termii, otherwise SMS will fail. The default <strong>Retailpulsepos</strong> is already registered and working.</p>
              <p>To register a custom name, contact your Termii account manager or email <strong>hello@termii.com</strong>.</p>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Custom Sender Name</label>
              <Input
                value={senderInput}
                onChange={e => setSenderInput(e.target.value.replace(/\s+/g, "").slice(0, 11))}
                placeholder="Only if registered with Termii"
                maxLength={11}
              />
              <p className="text-xs text-muted-foreground mt-1">{senderInput.length}/11 characters · no spaces</p>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              className="w-full sm:w-auto text-muted-foreground"
              onClick={() => handleSaveSender(true)}
              disabled={senderSaving}
            >
              Reset to default
            </Button>
            <Button onClick={() => handleSaveSender(false)} disabled={senderSaving || !senderInput.trim()} className="gap-2 w-full sm:w-auto">
              {senderSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Custom Name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Termii API key dialog */}
      <Dialog open={termiiKeyOpen} onOpenChange={setTermiiKeyOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Custom Termii API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {credits.hasCustomTermiiKey ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <p>Your own Termii API key is active. SMS sends go through your Termii account and are not billed by RetailPulse.</p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium mb-1">Use your own Termii account</p>
                <p>If you have a Termii account, paste your API key here. Your SMS will be sent through your account and deducted from your Termii balance directly.</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium block mb-1">Termii API Key</label>
              <Input
                value={termiiKeyInput}
                onChange={e => setTermiiKeyInput(e.target.value.trim())}
                placeholder={credits.hasCustomTermiiKey ? "Paste new key to replace" : "Paste your Termii API key"}
                className="font-mono text-xs"
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            {credits.hasCustomTermiiKey && (
              <Button variant="outline" className="w-full sm:w-auto text-muted-foreground" onClick={() => handleSaveTermiiKey(true)} disabled={termiiKeySaving}>
                Remove key
              </Button>
            )}
            <Button onClick={() => handleSaveTermiiKey(false)} disabled={termiiKeySaving || !termiiKeyInput.trim()} className="gap-2 w-full sm:w-auto">
              {termiiKeySaving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top-up dialog */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Top Up SMS Credits</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-muted rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Price per SMS</span><span className="font-medium">₦3</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Current balance</span><span className="font-medium">{credits.balance} credits</span></div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Credits to buy</label>
              <Input
                type="number"
                min="10"
                step="10"
                value={topupCredits}
                onChange={e => setTopupCredits(e.target.value)}
                placeholder="e.g. 100"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 10 credits</p>
            </div>
            {Number(topupCredits) >= 10 && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
                <div className="flex justify-between font-semibold">
                  <span>Total to pay</span>
                  <span>{formatCurrency(Number(topupCredits) * 3)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">You'll get {Number(topupCredits)} SMS credits after payment</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {[50, 100, 500, 1000].map(n => (
                <button
                  key={n}
                  onClick={() => setTopupCredits(String(n))}
                  className="text-xs px-2.5 py-1 rounded-md border hover:bg-muted transition-colors"
                >
                  {n} credits
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupOpen(false)}>Cancel</Button>
            <Button onClick={handleTopup} disabled={topupLoading} className="gap-2">
              {topupLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <Zap className="w-4 h-4" /> Pay with Paystack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
